import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/application/services/authService";
import { useTokens } from "@/design-system/hooks/useTokens";
import { PageLayout, Button, Card } from "@/ui/components/common";
import { ROUTES } from "@/domain/constants/routes";

/**
 * 個人資料頁面組件
 * 顯示用戶的個人資料
 */
export function ProfilePage() {
  const { user, logout, loading, error, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const tokens = useTokens();
  const navigate = useNavigate();

  // 確保過渡效果不為 undefined
  const getTransition = () => {
    if (tokens?.transition?.normal) {
      return tokens.transition.normal;
    } else if (tokens?.transitions?.normal) {
      return tokens.transitions.normal;
    } else {
      return "all 0.3s ease";
    }
  };

  // 當身份驗證狀態改變時處理重定向
  useEffect(() => {
    // 如果加載完成且未通過身份驗證，重定向到登入頁面
    if (!loading && !isAuthenticated) {
      navigate(ROUTES.LOGIN);
    }
  }, [loading, isAuthenticated, navigate]);

  // 顯示錯誤信息
  useEffect(() => {
    if (error) {
      console.error("身份驗證錯誤:", error);
      // 這裡可以添加顯示錯誤消息的邏輯，例如使用 toast 通知
    }
  }, [error]);

  // 如果正在加載或沒有用戶數據，顯示加載狀態
  if (loading) {
    return (
      <PageLayout title={t("user.profile")}>
        <div className="flex justify-center items-center py-12">
          <svg
            className="animate-spin h-10 w-10 text-primary-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
      </PageLayout>
    );
  }

  // 如果沒有用戶數據但已經通過身份驗證，可能是獲取用戶數據失敗
  if (!user && isAuthenticated) {
    return (
      <PageLayout title={t("user.profile")}>
        <Card elevation="medium">
          <div className="p-4 text-center">
            <p>{t("user.profile_error")}</p>
            <Button
              variant="primary"
              onClick={() => navigate(ROUTES.LOGIN)}
              className="mt-4"
            >
              {t("auth.back_to_login")}
            </Button>
          </div>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t("user.profile")}
      subtitle={t("user.welcome", { name: user?.name || "" })}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* 用戶資料卡 */}
        <Card elevation="medium" className="md:col-span-2">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <svg
                className="animate-spin h-8 w-8 text-primary-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          ) : (
            <div className="p-4">
              <div className="flex flex-col sm:flex-row items-center sm:items-start">
                {user?.profile_picture_url && (
                  <img
                    src={user.profile_picture_url}
                    alt="avatar"
                    className="rounded-full w-24 h-24 mb-4 sm:mb-0 sm:mr-6"
                    style={{
                      borderRadius: tokens.radius.full,
                      width: "6rem",
                      height: "6rem",
                      marginBottom: tokens.spacing.md,
                    }}
                  />
                )}
                <div className="text-center sm:text-left">
                  <h2
                    className="text-xl font-bold text-gray-900 dark:text-white"
                    style={{
                      fontSize: tokens.typography.size.xl,
                      fontWeight: tokens.typography.weight.bold,
                      color: tokens.colors.text.primary,
                    }}
                  >
                    {user?.name}
                  </h2>
                  <p
                    className="text-gray-500 dark:text-gray-300"
                    style={{
                      color: tokens.colors.text.secondary,
                    }}
                  >
                    {user?.email}
                  </p>
                </div>
              </div>

              <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3
                  className="text-lg font-semibold mb-2"
                  style={{
                    fontSize: tokens.typography.size.lg,
                    fontWeight: tokens.typography.weight.semibold,
                    marginBottom: tokens.spacing.sm,
                  }}
                >
                  {t("user.profile")}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t("user.id")}
                    </p>
                    <p>{user?.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t("user.joined")}
                    </p>
                    <p>
                      {user?.created_at
                        ? new Date(user.created_at).toLocaleDateString()
                        : "-"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* 操作卡 */}
        <Card elevation="medium">
          <div className="p-4">
            <h3
              className="text-lg font-semibold mb-4"
              style={{
                fontSize: tokens.typography.size.lg,
                fontWeight: tokens.typography.weight.semibold,
                marginBottom: tokens.spacing.md,
              }}
            >
              {t("actions.title")}
            </h3>

            <div className="space-y-3">
              <Button variant="outlined" fullWidth onClick={() => {}}>
                {t("user.edit_profile")}
              </Button>

              <Button variant="outlined" fullWidth onClick={() => {}}>
                {t("user.change_password")}
              </Button>

              <Button
                variant="text"
                fullWidth
                onClick={logout}
                isLoading={loading}
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                style={{ transition: getTransition() }}
              >
                {t("auth.logout")}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </PageLayout>
  );
}
